<script setup lang="ts">
    import { computed } from 'vue'
    import { EditorState } from '@common/Common'
    import { DiscreteScaleLevelReadModel } from '@Scales/readmodels'
    import type { DiscreteScaleEditorViewModel } from '@Scales/Components/DiscreteScaleEditor'

    /* props */
    const props = defineProps<{
        viewModel: DiscreteScaleEditorViewModel
    }>()

    /* emits */
    const emit = defineEmits<{
        (e: 'create-scale', name: string | null, description: string | null, levels: string): void
        (e: 'update-scale', id: number | null, name: string | null, description: string | null, levels: string): void
        (e: 'delete-scale', id: number | null): void
    }>()

    /* shortcuts */
    const vm = computed(() => props.viewModel)

    /* derived state */
    const opened = computed(() => vm.value?.opened ?? false)

    const confirmButtonLabel = computed(() =>
        vm.value.state === EditorState.Creator ? 'Create' : 'Update'
    )

    const modalTitle = computed(() =>
        vm.value.state === EditorState.Creator ? 'Create scale' : 'Edit scale'
    )

    const canDelete = computed(() =>
        vm.value.state === EditorState.Editor
    )

    const nameError = computed(() => vm.value.nameError)
    const descriptionError = computed(() => vm.value.descriptionError)
    const levelsError = computed(() => vm.value.levelsError)

    const levels = computed(() => vm.value.levels)

    /* v-model proxies */

    const name = computed({
        get: () => vm.value.name,
        set: v => (vm.value.name = v)
    })

    const description = computed({
        get: () => vm.value.description,
        set: v => (vm.value.description = v)
    })

    /* actions */

    function removeScaleLevel(lvl: DiscreteScaleLevelReadModel)
    {
        vm.value.levels.splice(vm.value.levels.indexOf(lvl), 1)
    }

    function addScaleLevel()
    {
        vm.value.levels.push(new DiscreteScaleLevelReadModel())
    }

    function handleFormSubmit()
    {
        vm.value.clearValidationErrors()

        if (vm.value.state === EditorState.Creator)
        {
            emit('create-scale', name.value, description.value, JSON.stringify(levels.value))
        } else
        {
            emit(
                'update-scale',
                vm.value.scaleId,
                name.value,
                description.value,
                JSON.stringify(levels.value)
            )
        }
    }

    function handleDeleteScale()
    {
        emit('delete-scale', vm.value.scaleId)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <form id="discrete-scale-editor-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Name:</label>
                                <input type="text" v-model.trim="name" class="form-control" required minlength="2" maxlength="100" />
                                <div class="error">{{nameError}}</div>
                            </div>

                            <div class="form-group">
                                <label>Description:</label>
                                <textarea v-model.trim="description" rows="3" class="form-control"></textarea>
                                <div class="error">{{descriptionError}}</div>
                            </div>

                            <div class="row" style="margin-bottom: 5px;">
                                <div class="col-sm-3">
                                    <label>Scale level <span class="text-danger">*</span></label>
                                </div>
                                <div class="col-sm-2">
                                    <label>Value <span class="text-danger">*</span></label>
                                </div>
                                <div class="col-sm-6">
                                    <label>Definition</label>
                                </div>
                            </div>

                            <div v-for="l in levels">
                                <div class="row" style="margin-bottom: 5px;">
                                    <div class="col-sm-3">
                                        <input type="text" v-model.trim="l.Name" class="form-control" placeholder="Level" required minlength="2" maxlength="100">
                                        <div class="error">{{l.NameError}}</div>
                                    </div>
                                    <div class="col-sm-2">
                                        <input type="number" v-model.number="l.Value" min="0" placeholder="Number..." class="form-control" required>
                                        <div class="error">{{l.ValueError}}</div>
                                    </div>
                                    <div class="col-sm-6">
                                        <textarea type="text" v-model.trim="l.Definition" rows="1" class="form-control" placeholder="Definition for level"></textarea>
                                    </div>
                                    <div class="col-sm-1">
                                        <a @click="removeScaleLevel(l)" class="pointer fa fa-times"></a>
                                    </div>
                                </div>
                            </div>
                            <div class="error">{{levelsError}}</div>

                            <p>
                                <a class="text-primary" @click="addScaleLevel">Add scale level</a>
                            </p>
                        </form>
                    </div>

                    <div class="modal-footer justify-content-between">
                        <div class="dropdown dropup">
                            <button type="button" v-if="canDelete" class="btn btn-danger" data-toggle="dropdown">
                                Delete
                            </button>
                            <div class="dropdown-menu" style="padding: 10px;">
                                <a class="dropdown-item" @click="handleDeleteScale">Confirm</a>
                                <a class="dropdown-item">Cancel</a>
                            </div>
                        </div>
                        <div>
                            <button type="button" @click="close" class="btn btn-outline-secondary mr-1">Cancel</button>
                            <button type="submit" form="discrete-scale-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
